import { useState, useEffect } from "react";
import { useLang } from "../../i18n/LanguageContext";

export default function PartnerModal({ partner: p, onClose }) {
  const { t, isRTL } = useLang();
  const [tab, setTab] = useState("about");
  const tabs = ["about","products","certificates","applications"];

  // close on Escape
  useEffect(() => {
    const fn = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  const name      = p.name;
  const about     = isRTL ? p.aboutAr : p.about;
  const tagline   = isRTL ? p.taglineAr : p.tagline;
  const category  = isRTL ? p.categoryAr : p.category;
  const products  = isRTL ? (p.productsAr || p.products) : p.products;
  const apps      = isRTL ? (p.applicationsAr || p.applications) : p.applications;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose} role="dialog" aria-modal="true">
      <div className="relative bg-[#111112] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="sticky top-0 bg-[#111112]/95 backdrop-blur-sm border-b border-white/5 p-5 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-sm font-black text-black flex-shrink-0"
              style={{ backgroundColor: p.accent }}>{p.logoText}</div>
            <div>
              <h3 className="text-white font-black text-lg">{name}</h3>
              <div className="text-zinc-500 text-[10px] uppercase tracking-wider flex items-center gap-1">
                <i className={`fas ${p.categoryIcon} text-[#F28C18] text-[9px]`} /> {category}
              </div>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
            <i className="fas fa-times text-xs" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 py-3 bg-[#0D0D0E] border-b border-white/5 overflow-x-auto">
          {tabs.map(tb => (
            <button key={tb} onClick={() => setTab(tb)}
              className={`px-4 py-2 rounded text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all ${
                tab === tb ? "bg-[#F28C18] text-black" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
              {t.partners[tb] || tb}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {tab === "about" && (
            <div className="space-y-5">
              <div className="p-4 rounded-lg bg-white/5 border border-white/5">
                <div className="text-[#F28C18] text-[10px] font-black uppercase tracking-widest mb-1">Official Tagline</div>
                <div className="text-white font-bold text-sm">{tagline}</div>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">{about}</p>
              <a href={p.catalog}
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#F28C18] text-black text-xs font-black uppercase tracking-widest rounded-lg hover:bg-white transition-all">
                <i className="fas fa-download" /> {t.partners.downloadCatalog}
              </a>
            </div>
          )}

          {tab === "products" && (
            <div className="space-y-2">
              {products.map(prod => (
                <div key={prod} className="flex items-center gap-3 p-3.5 bg-white/5 rounded-lg border border-white/5">
                  <i className="fas fa-check text-[#F28C18] text-xs flex-shrink-0" />
                  <span className="text-zinc-300 text-sm">{prod}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "certificates" && (
            <div className="grid sm:grid-cols-2 gap-3">
              {p.certificates.map(cert => (
                <div key={cert} className="p-4 rounded-lg bg-[#1A1A1C] border border-white/5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-certificate text-[#F28C18] text-xs" />
                  </div>
                  <span className="text-white text-xs font-black">{cert}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "applications" && (
            <div className="grid sm:grid-cols-2 gap-3">
              {apps.map(app => (
                <div key={app} className="p-4 rounded-lg bg-[#1A1A1C] border border-white/5 flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#F28C18] flex-shrink-0" />
                  <span className="text-zinc-300 text-sm">{app}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
